import {Alert, Button, Drawer, Group, NumberInput, Paper, Radio, Stack, Text} from '@mantine/core'
import {IconTruck} from '@tabler/icons-react'
import {useEffect, useState} from 'react'

import {DriverValidationError} from 'src/driver-view/driver-fetch'
import type {DriverJob} from 'src/driver-view/schemas'

import {deliveryCountsLabel} from './delivery-counts-label'

type DriverStockEquipmentGroup = DriverJob['stockEquipmentGroups'][number]
type DriverJobLocation = DriverJob['locations'][number]

type DriverStockEquipmentCardProps = {
    group: DriverStockEquipmentGroup
    locations: DriverJobLocation[]
    deploying: boolean
    onDeliver?: (input: {jobLocationId: string; quantity: number}) => Promise<{
        assignedQuantity: number
        deliveredQuantity: number
        remainingQuantity: number
        deliveredByLocation: DriverStockEquipmentGroup['deliveredByLocation']
    }>
    isPickup?: boolean
}

export function DriverStockEquipmentCard({
    group,
    locations,
    deploying,
    onDeliver,
    isPickup = false,
}: DriverStockEquipmentCardProps) {
    const [displayGroup, setDisplayGroup] = useState(group)
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
        locations.length === 1 ? (locations[0]?.id ?? null) : null,
    )
    const [quantity, setQuantity] = useState<number | string>(1)
    const [quantityError, setQuantityError] = useState<string | null>(null)
    const [formError, setFormError] = useState<string | null>(null)

    useEffect(() => {
        setDisplayGroup(group)
    }, [group])

    const canDeliver =
        onDeliver != null && !isPickup && displayGroup.remainingQuantity > 0 && locations.length > 0
    const returned = displayGroup.returnedQuantity

    function openDrawer() {
        setSelectedLocationId(locations.length === 1 ? (locations[0]?.id ?? null) : null)
        setQuantity(1)
        setQuantityError(null)
        setFormError(null)
        setDrawerOpen(true)
    }

    async function handleConfirm() {
        const parsedQuantity = typeof quantity === 'number' ? quantity : Number(quantity)
        if (!onDeliver) {
            return
        }
        if (!selectedLocationId) {
            setFormError('Select a delivery location')
            return
        }
        if (!Number.isInteger(parsedQuantity) || parsedQuantity < 1) {
            setQuantityError('Must be at least 1')
            return
        }

        setQuantityError(null)
        setFormError(null)

        try {
            const result = await onDeliver({
                jobLocationId: selectedLocationId,
                quantity: parsedQuantity,
            })
            setDisplayGroup((current) => ({
                ...current,
                assignedQuantity: result.assignedQuantity,
                deliveredQuantity: result.deliveredQuantity,
                remainingQuantity: result.remainingQuantity,
                deliveredByLocation: result.deliveredByLocation,
            }))
            setDrawerOpen(false)
        } catch (error) {
            if (error instanceof DriverValidationError) {
                setQuantityError(error.fieldErrors['quantity'] ?? null)
                const otherFieldError = Object.entries(error.fieldErrors).find(
                    ([field]) => field !== 'quantity',
                )
                setFormError(otherFieldError?.[1] ?? null)
                return
            }
            setFormError(error instanceof Error ? error.message : 'Delivery failed')
        }
    }

    return (
        <>
            <Paper withBorder p="md" radius="md">
                <Stack gap="sm">
                    <Group gap="sm" wrap="nowrap">
                        <IconTruck size={22} stroke={1.5} />
                        <Stack gap={2} style={{minWidth: 0}}>
                            <Text size="sm" fw={600}>
                                {displayGroup.rentalAssetTypeName}
                                {displayGroup.thirdPartyRentalCompanyName
                                    ? ` · ${displayGroup.thirdPartyRentalCompanyName}`
                                    : ''}
                            </Text>
                            <Text size="sm" c="dimmed">
                                {isPickup
                                    ? `To collect ${displayGroup.deliveredQuantity}${returned > 0 ? ` · Collected ${returned}` : ''}`
                                    : deliveryCountsLabel(displayGroup)}
                            </Text>
                        </Stack>
                    </Group>

                    {displayGroup.deliveredByLocation.length > 0 ? (
                        <Stack gap={4}>
                            {displayGroup.deliveredByLocation.map((location) => (
                                <Text
                                    key={location.jobLocationId}
                                    size="sm"
                                    c={isPickup ? undefined : 'green.7'}
                                >
                                    {isPickup ? 'At ' : ''}
                                    {location.locationName} · {location.quantity}
                                </Text>
                            ))}
                        </Stack>
                    ) : null}

                    {canDeliver ? (
                        <Button fullWidth onClick={openDrawer} loading={deploying}>
                            Deliver
                        </Button>
                    ) : null}
                </Stack>
            </Paper>

            <Drawer
                opened={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                position="bottom"
                title={`Deliver ${displayGroup.rentalAssetTypeName}`}
            >
                <Stack gap="md">
                    {formError ? (
                        <Alert color="red" title="Unable to deliver">
                            {formError}
                        </Alert>
                    ) : null}

                    {locations.length > 1 ? (
                        <Radio.Group
                            value={selectedLocationId}
                            onChange={setSelectedLocationId}
                            name="stock-delivery-location"
                            label="Delivery location"
                        >
                            <Stack gap="xs">
                                {locations.map((location) => (
                                    <Radio
                                        key={location.id}
                                        value={location.id}
                                        label={location.name}
                                    />
                                ))}
                            </Stack>
                        </Radio.Group>
                    ) : locations.length === 1 ? (
                        <Text size="sm" fw={500}>
                            {locations[0]?.name}
                        </Text>
                    ) : null}

                    <NumberInput
                        label="Quantity"
                        value={quantity}
                        onChange={setQuantity}
                        min={1}
                        max={displayGroup.remainingQuantity}
                        allowDecimal={false}
                        error={quantityError}
                    />

                    <Group justify="flex-end" gap="sm">
                        <Button variant="default" onClick={() => setDrawerOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={() => void handleConfirm()} loading={deploying}>
                            Confirm delivery
                        </Button>
                    </Group>
                </Stack>
            </Drawer>
        </>
    )
}
